import type { DbCharacter, DbCharacterProfile } from '@/api/astraea';

interface ProfileSummaryProps {
  character?: Pick<DbCharacter, 'description' | 'business_goal'> | DbCharacter | null;
  profile?: DbCharacterProfile | null;
  compact?: boolean;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-2.5 border-b border-secondary-100 last:border-0">
      <div className="text-[13px] sm:text-xs font-semibold text-primary-600 mb-1">{label}</div>
      <div className="text-[15px] sm:text-sm text-foreground-800 leading-relaxed">{children}</div>
    </div>
  );
}

/** 角色人设摘要 — 手机端加大字号与行距,方便阅读 */
export default function ProfileSummary({ character, profile, compact }: ProfileSummaryProps) {
  const tags = profile?.persona_tags ?? [];
  const avoid = profile?.avoid_words ?? [];
  const audience = profile?.target_audience as Record<string, unknown> | undefined;
  const core = Array.isArray(audience?.core) ? (audience.core as string[]) : [];
  const pains = Array.isArray(audience?.pain_points) ? (audience.pain_points as string[]) : [];

  if (!character?.description && !profile) return null;

  return (
    <div className={`space-y-0 ${compact ? '' : 'rounded-xl bg-background-100/80'}`}>
      {character?.description && <Row label="一句话定位">{character.description}</Row>}
      {profile?.role_positioning && <Row label="角色定位">{profile.role_positioning}</Row>}
      {tags.length > 0 && (
        <Row label="人格标签">
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-800 text-[13px] sm:text-xs font-medium">{t}</span>
            ))}
          </div>
        </Row>
      )}
      {core.length > 0 && (
        <Row label="目标受众">
          {core.join('、')}
          {pains.length > 0 && <div className="mt-1.5 text-[14px] sm:text-[13px] text-foreground-600">痛点:{pains.join('、')}</div>}
        </Row>
      )}
      {profile?.tone && <Row label="语气">{profile.tone}</Row>}
      {profile?.speaking_style && <Row label="说话方式">{profile.speaking_style}</Row>}
      {character?.business_goal && <Row label="变现方向">{character.business_goal}</Row>}
      {avoid.length > 0 && (
        <Row label="禁用词">
          <span className="text-red-600">{avoid.join('、')}</span>
        </Row>
      )}
    </div>
  );
}
